import { endOfDay, min, subDays } from "date-fns";
import type { DateRange, DateRangeType } from "@/types/common";
import type { SelectedRange } from "@/types/data/redux.types";

export const TIME_BUFFER = 5 * 60 * 60 * 1000; //5 hrs
export const THIRTY_MINUTES_IN_MILLISECONDS = 1000 * 60 * 30;
export const ONE_HOUR_IN_MILLISECONDS = 1000 * 60 * 60;
export const ONE_DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;
export const ONE_WEEK_IN_MILLISECONDS = 7 * 1000 * 60 * 60 * 24;
export const EIGHT_WEEKS_IN_MILLISECONDS = 838400000;
export const QUERY_DATE_FORMAT = "yyyy-MM-dd";
export const QUERY_DATE_FORMAT_WITH_TIME_AND_TIMEZONE = "yyyy-MM-dd HH:mm zzz";
export const USER_DATE_FORMAT = "dd/MM/yyyy";
export const USER_DATE_FORMAT_WITH_TIME = "dd/MM/yyyy HH:mm";
export const USER_DATE_FORMAT_WITH_TIME_AND_TIMEZONE =
  "dd/MM/yyyy HH:mm:ss zzz";
export const USER_DATE_FORMAT_SHORT = "dd/MM/yy";
export const USER_TIME_FORMAT_SHORT = "H:mm";
export const USER_DATE_FORMAT_SHORT_TIME_LINE = "dd/MM/yy HH:mm";
export const USER_DATE_TIME_MILLISECOND_FORMAT = "dd MMM, HH:mm:ss.SSS";

export type PresetDateRangeItem = {
  value: DateRangeType;
  name?: string;
  dateRange?: {
    from: Date | undefined;
    to: Date | undefined;
  };
};

export const RANGE_NAMES = {
  efaDay: "EFA Day",
  efaDayPlusOne: "EFA Day +1hr",
  last7days: "Last 7 days",
  previousWeek: "Previous week",
  previousMonth: "Previous month",
  last3months: "Last 3 months",
  last6months: "Last 6 months",
  yearToDate: "Year to date",
} as const;

export type SaveDate = {
  range: Partial<DateRange<Date>>;
  rangeType: string;
};

export const presetDateRangeList = (
  dateRange: SelectedRange
): PresetDateRangeItem[] => {
  const toDateInstance = (value?: string) =>
    value ? new Date(value) : undefined;

  return [
    {
      value: "live",
      dateRange: {
        from: toDateInstance(dateRange?.from),
        to: toDateInstance(dateRange?.to),
      },
    },
    { value: "efaDay", name: RANGE_NAMES.efaDay },
    { value: "efaDayPlusOne", name: RANGE_NAMES.efaDayPlusOne },
    { value: "today" },
    { value: "yesterday" },
    { value: "tomorrow" },
  ];
};

export const presetDateRangeListBm = (dateRange: SelectedRange) => {
  const toDateInstance = (value?: string) =>
    value ? new Date(value) : undefined;

  return [
    { value: "today" },
    { value: "yesterday" },
    { value: "last7days", name: RANGE_NAMES.last7days },
    {
      value: "custom",
      dateRange: {
        from: toDateInstance(dateRange.from),
        to: toDateInstance(dateRange.to),
      },
    },
  ];
};

const toDateInstance = (value?: string) =>
  value ? new Date(value) : undefined;

export const presetDateRangeListExplorer = (
  dateRange: SelectedRange,
  dayOffset = 0
) => {
  const customRange = {
    from: toDateInstance(dateRange.from),
    to: toDateInstance(dateRange.to),
  };
  const disabledAfter = endOfDay(subDays(new Date(), dayOffset));

  if (customRange.to) {
    customRange.to = min([customRange.to, disabledAfter]);
  }

  return [
    { value: "yesterday" },
    { value: "last7days", name: RANGE_NAMES.last7days },
    { value: "previousMonth", name: RANGE_NAMES.previousMonth },
    { value: "last3months", name: RANGE_NAMES.last3months },
    { value: "last6months", name: RANGE_NAMES.last6months },
    { value: "yearToDate", name: RANGE_NAMES.yearToDate },
    { value: "custom", dateRange: customRange },
  ];
};
